import { Spine } from "@esotericsoftware/spine-pixi-v8";
import * as PIXI from "pixi.js";
import { useEffect, useRef } from "preact/hooks";
import { buildAssetUrl } from "../../pixi/assetsInit";
import css from "./Loader.module.scss";

const CANVAS_WIDTH = 140;
const CANVAS_HEIGHT = 180;

const GAME_TYPE = import.meta.env.VITE_GAME_TYPE || "twins";

interface LoaderProps {
  variant?: "fullscreen" | "overlay";
}

export const CssLoader = () => {
  return (
    <div className={css.containerOverlay}>
      <div className={css.spinner} />
      <p>Loading</p>
    </div>
  );
};

const getLoadingPaths = () => {
  const gamePrefix = GAME_TYPE === "pioneer" ? "pioneer-game" : "twins-game";
  const basePath = `/spineOptimized/${gamePrefix}/loading/loading`;
  return {
    skeleton: buildAssetUrl(`${basePath}.json`),
    atlas: buildAssetUrl(`${basePath}.webp.atlas`),
  };
};

export const SpineLoader = () => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const appRef = useRef<PIXI.Application | null>(null);
  const destroyedRef = useRef(false);
  const initStartedRef = useRef(false);

  useEffect(() => {
    if (initStartedRef.current) return;
    initStartedRef.current = true;
    destroyedRef.current = false;

    const init = async () => {
      if (!canvasRef.current || destroyedRef.current) return;

      const app = new PIXI.Application();
      const dpr = Math.max(window.devicePixelRatio || 1, 2);

      try {
        await app.init({
          canvas: canvasRef.current,
          width: CANVAS_WIDTH,
          height: CANVAS_HEIGHT,
          backgroundAlpha: 0,
          antialias: true,
          resolution: dpr,
          preference: "webgl",
          powerPreference: "high-performance",
        } as any);
      } catch (e) {
        console.warn("Loader: app init failed", e);
        return;
      }

      if (destroyedRef.current) {
        try {
          app.destroy(true);
        } catch {
          /* noop */
        }
        return;
      }

      appRef.current = app;

      try {
        const paths = getLoadingPaths();

        if (destroyedRef.current) return;

        await PIXI.Assets.load([paths.skeleton, paths.atlas]);

        if (destroyedRef.current) return;

        const spine = Spine.from({
          skeleton: paths.skeleton,
          atlas: paths.atlas,
          scale: 1,
          autoUpdate: true,
        });

        if (destroyedRef.current) return;

        app.stage.addChild(spine);

        try {
          spine.state.apply(spine.skeleton);
        } catch {
          void 0;
        }
        spine.update(0);
        const w = app.renderer.width;
        const h = app.renderer.height;
        const lb = spine.getLocalBounds();
        const s = Math.min(w / lb.width, h / lb.height);
        spine.scale.set(s);
        spine.x = -lb.x * s + (w - lb.width * s) / 2;
        spine.y = -lb.y * s + (h - lb.height * s) / 2;

        try {
          spine.state.setAnimation(0, "loading", true);
        } catch {
          void 0;
        }

        if (!destroyedRef.current) {
          app.ticker.start();
        }
      } catch (e) {
        console.warn("Loader: spine init failed", e);
      }
    };

    init();

    return () => {
      destroyedRef.current = true;
      const app = appRef.current;
      appRef.current = null;

      if (app) {
        try {
          if (app.ticker) {
            app.ticker.stop();
            app.ticker.destroy();
          }
        } catch {
          /* noop */
        }

        // Destroy app synchronously
        try {
          app.destroy(true, { children: true });
        } catch {
          // Ignore destroy errors
        }
      }
    };
  }, []);

  return (
    <div className={css.container}>
      <canvas
        ref={canvasRef}
        style={{ width: CANVAS_WIDTH, height: CANVAS_HEIGHT, display: "block" }}
      />
      <p className={GAME_TYPE === "twins" ? css.loadingText : ""}>Loading</p>
    </div>
  );
};

export const Loader = ({ variant = "fullscreen" }: LoaderProps) => {
  if (variant === "overlay") {
    return <CssLoader />;
  }
  return <SpineLoader />;
};
